import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { VendorChatService } from '../service/vendor-chat.service';
import { VendorChatMessage } from '../models/VendorChatMessage';
import { NotificationService } from '../service/notification.service';
import { containsProfanity, PROFANITY_MESSAGE } from '../util/profanity';
import { containsLink, LINK_MESSAGE } from '../util/chat-content';

const CHAT_EMOJIS = [
  '😀', '😄', '😊', '🙂', '😉', '😍', '🤗', '🤔',
  '👍', '👎', '👋', '🙏', '👏', '💪', '🤝', '✌️',
  '❤️', '🎉', '✅', '❌', '⭐', '🔥', '💡', '📌',
  '📄', '📎', '📅', '⏰', '💼', '🏢', '💰', '❓',
];

@Component({
  selector: 'app-vendor-chat',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './vendor-chat.component.html',
  styleUrls: ['./vendor-chat.component.css'],
})
export class VendorChatComponent implements OnInit, OnDestroy {
  @ViewChild('messageList') messageList: ElementRef;

  messages: VendorChatMessage[] = [];
  newMessageText = '';
  sending = false;
  loading = true;
  composerError = '';
  vendorId: string | null = null;
  reactionPickerFor: number | null = null;
  readonly reactionOptions = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
  readonly emojis = CHAT_EMOJIS;
  emojiOpen = false;
  private pollHandle: any;
  private readonly pollIntervalMs = 20000;

  constructor(
    private vendorChatService: VendorChatService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.vendorId = JSON.parse(localStorage.getItem('vendorId') || 'null');
    if (!this.vendorId) {
      this.loading = false;
      return;
    }
    this.loadMessages(true);
    this.pollHandle = setInterval(() => this.loadMessages(false), this.pollIntervalMs);
  }

  ngOnDestroy(): void {
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
    }
  }

  loadMessages(scrollToBottom: boolean): void {
    if (!this.vendorId) return;
    this.vendorChatService.getMessages(this.vendorId).subscribe({
      next: (messages) => {
        const hadNew = messages.length !== this.messages.length;
        this.messages = messages;
        this.loading = false;
        if (messages.some((m) => m.senderSide === 'Staff' && !m.isReadByVendor)) {
          this.vendorChatService.markMessagesRead(this.vendorId!).subscribe();
        }
        if (scrollToBottom || hadNew) {
          this.scrollToBottom();
        }
      },
      error: () => {
        this.loading = false;
        this.notificationService.showError('Unable to load chat messages.');
      },
    });
  }

  sendMessage(): void {
    const text = this.newMessageText.trim();
    if (!text || !this.vendorId || this.sending) return;
    if (containsProfanity(text)) {
      this.composerError = PROFANITY_MESSAGE;
      return;
    }
    if (containsLink(text)) {
      this.composerError = LINK_MESSAGE;
      return;
    }
    this.sending = true;
    this.emojiOpen = false;
    this.vendorChatService.sendMessage(this.vendorId, text).subscribe({
      next: (message) => {
        this.appendMessage(message);
        this.newMessageText = '';
        this.sending = false;
        this.composerError = '';
        this.scrollToBottom();
      },
      error: () => {
        this.sending = false;
        this.composerError = 'Unable to send message. Please try again.';
      },
    });
  }

  onComposerInput(): void {
    if (this.composerError) {
      this.composerError = '';
    }
  }

  toggleEmoji(): void {
    this.emojiOpen = !this.emojiOpen;
  }

  addEmoji(emoji: string): void {
    this.newMessageText = `${this.newMessageText}${emoji}`;
  }

  toggleReactionPicker(messageId: number): void {
    this.reactionPickerFor = this.reactionPickerFor === messageId ? null : messageId;
  }

  react(message: VendorChatMessage, emoji: string): void {
    if (!this.vendorId) return;
    const next = message.vendorReaction === emoji ? null : emoji;
    this.reactionPickerFor = null;
    this.vendorChatService.setReaction(this.vendorId, message.id, next).subscribe({
      next: (updated) => {
        message.vendorReaction = updated.vendorReaction;
        message.staffReaction = updated.staffReaction;
      },
      error: () => {
        this.composerError = 'Unable to update the reaction. Please try again.';
      },
    });
  }

  /**
   * A poll started before the send can land after it and re-add the same row, which
   * rendered the sent message twice until the page reloaded.
   */
  private appendMessage(message: VendorChatMessage): void {
    if (this.messages.some((existing) => this.isSameMessage(existing, message))) return;
    this.messages = [...this.messages, message];
  }

  private isSameMessage(existing: VendorChatMessage, incoming: VendorChatMessage): boolean {
    if (existing.id && incoming.id && existing.id === incoming.id) return true;
    return (
      existing.senderSide === incoming.senderSide &&
      existing.text === incoming.text &&
      existing.createdDate === incoming.createdDate
    );
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.messageList) {
        this.messageList.nativeElement.scrollTop = this.messageList.nativeElement.scrollHeight;
      }
    });
  }
}
